import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { userAPI, authAPI, campaignAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { CheckCircle, Circle, User, Share2, Target, FileText, ChevronDown, ChevronUp, X, Sparkles } from 'lucide-react';
import './OnboardingChecklist.css';

function OnboardingChecklist() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Check if user dismissed onboarding (persist in localStorage)
  const storageKey = `onboarding-dismissed-${user?.id}`;
  const wasDismissed = localStorage.getItem(storageKey) === 'true';

  // Fetch profile data
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => userAPI.getProfile().then(res => res.data.user),
  });

  // Fetch social accounts
  const { data: socialData } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: () => authAPI.getSocialAccounts().then(res => res.data),
  });

  // Fetch user stats (tasks, posts)
  const { data: stats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => userAPI.getStats().then(res => res.data),
  });

  // Fetch campaigns to check if user joined any
  const { data: campaigns } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignAPI.getAll().then(res => res.data.campaigns || res.data),
  });

  if (wasDismissed || dismissed) return null;

  // Calculate checklist items
  const profileComplete = !!(profile?.bio && profile?.phone);
  const socialConnected = !!(socialData?.accounts?.length > 0);
  const campaignJoined = !!(campaigns && campaigns.length > 0 && stats?.tasks?.completed > 0);
  const firstPost = !!(stats?.social?.posts > 0);

  const steps = [
    {
      id: 'profile',
      label: 'Complete your profile',
      description: 'Add your bio and phone number',
      done: profileComplete,
      action: () => navigate('/profile'),
      icon: <User size={16} />,
    },
    {
      id: 'social',
      label: 'Connect a social account',
      description: 'Link Facebook, YouTube, or another platform',
      done: socialConnected,
      action: () => navigate('/social'),
      icon: <Share2 size={16} />,
    },
    {
      id: 'campaign',
      label: 'Complete your first task',
      description: 'Browse tasks and complete one to earn points',
      done: campaignJoined,
      action: () => navigate('/tasks'),
      icon: <Target size={16} />,
    },
    {
      id: 'post',
      label: 'Submit your first post',
      description: 'Track a social media post for a campaign',
      done: firstPost,
      action: () => navigate('/track-post'),
      icon: <FileText size={16} />,
    },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const allDone = completedCount === steps.length;
  const progress = (completedCount / steps.length) * 100;

  // Auto-dismiss if all done
  if (allDone) {
    return (
      <div className="onboarding-card onboarding-complete">
        <div className="onboarding-complete-content">
          <Sparkles size={24} color="#10b981" />
          <div>
            <h3>You're all set!</h3>
            <p>You've completed all getting-started steps. Happy ambassadoring!</p>
          </div>
        </div>
        <button
          className="onboarding-dismiss"
          onClick={() => { localStorage.setItem(storageKey, 'true'); setDismissed(true); }}
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="onboarding-card">
      <div className="onboarding-header" onClick={() => setExpanded(!expanded)}>
        <div className="onboarding-title-row">
          <div>
            <h3>Getting Started</h3>
            <p>{completedCount} of {steps.length} completed</p>
          </div>
          <div className="onboarding-header-right">
            <button
              className="onboarding-dismiss"
              onClick={(e) => { e.stopPropagation(); localStorage.setItem(storageKey, 'true'); setDismissed(true); }}
              title="Dismiss checklist"
            >
              <X size={16} />
            </button>
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </div>
        <div className="onboarding-progress-bar">
          <div className="onboarding-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {expanded && (
        <div className="onboarding-steps">
          {steps.map(step => (
            <div
              key={step.id}
              className={`onboarding-step ${step.done ? 'step-done' : ''}`}
              onClick={!step.done ? step.action : undefined}
            >
              <div className="step-check">
                {step.done ? (
                  <CheckCircle size={20} color="#10b981" />
                ) : (
                  <Circle size={20} color="#d1d5db" />
                )}
              </div>
              <div className="step-icon">{step.icon}</div>
              <div className="step-info">
                <div className="step-label">{step.label}</div>
                <div className="step-desc">{step.description}</div>
              </div>
              {!step.done && <span className="step-arrow">&rarr;</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OnboardingChecklist;
